import { ReactComponent as AddSvg } from '@/images/add.svg';
import { ReactComponent as AddIcon } from '@/images/add_icon.svg';
import { ReactComponent as BackSvg } from '@/images/back.svg';
import { ReactComponent as CloseSvg } from '@/images/cross.svg';
import csSvg from '@/images/icon_cs.svg';
import { ReactComponent as NewBackSvg } from '@/images/icon_new_back.svg';
import questionPng from '@/images/question.png';
import { ReactComponent as EditSvg } from '@/pages/BillMgmt/subpages/ledgerDetails/images/icon_edit.svg';
import { t } from 'i18next';
import { noop, omit } from 'lodash-es';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Color } from '../../../types/color';
import usePcH5 from '../../hooks/usePcH5';
import useSafeBarHeight from '../../hooks/useStatusBarHeight';
import type { RouteItem } from '../../routes';
import { getEnValue, getZhValue, px2vw } from '../../utils';
import { getPrimaryColor } from '../../utils/colorUtils';
import { kkBridgeRegwithdata, kkBridgeUnRegwithdata } from '../../utils/jsBridge';
import { nativeApi } from '../../utils/nativeApi';
import { Platform } from '../../utils/nativeApi/interface';
import { updateDocumentTitle } from '../../utils/util';
import FloatingAssistant from './FloatingAssistant';
import './index.less';

import { LayoutCompContext, LayoutContext } from './context';

import {
  BackBtnType,
  ForceDisplay,
  HeaderFontStyle,
  LayoutPropsType,
  PageConfig,
} from './interface';

export { LayoutCompContext, LayoutContext } from './context';

const prefixCls = 'vhk-layout';

interface clickFunc {
  (): boolean | void;
}

function FinishComp({ onClick, color }: { onClick: clickFunc; color: Color }) {
  return (
    <div className={`${prefixCls}-header-right`} style={{ color }} onClick={onClick}>
      {t('common_finish')}
    </div>
  );
}

function TextComp({
  onClick,
  color,
  text,
  clsName,
}: {
  onClick: clickFunc;
  color: Color;
  text: string;
  clsName?: string | undefined;
}) {
  return (
    <div className={`${prefixCls}-header-right ${clsName}`} style={{ color }} onClick={onClick}>
      {text}
    </div>
  );
}

function QuestionComp(props: React.Attributes) {
  return <img alt="" src={questionPng} {...omit(props, ['clsName'])} />;
}

function CustomerServiceComp(props: React.Attributes) {
  return <img alt="" src={csSvg} {...omit(props, ['clsName'])} />;
}

function BackComp(props: React.Attributes & { style: React.CSSProperties }) {
  const { style } = props;
  // 为BackComp创建独立的px2vw缓存
  const backCompPx2vw = useMemo(
    () => ({
      size24: px2vw(24),
      sizeNeg4: px2vw(-4),
    }),
    []
  );

  return nativeApi.isNewVersion ? (
    <NewBackSvg
      {...props}
      style={{
        ...style,
        width: backCompPx2vw.size24,
        height: backCompPx2vw.size24,
        marginLeft: backCompPx2vw.sizeNeg4,
      }}
    />
  ) : (
    <BackSvg {...props} style={{ ...style, marginLeft: backCompPx2vw.sizeNeg4 }} />
  );
}

const typeIconMap = {
  [BackBtnType.Back]: BackComp,
  [BackBtnType.Close]: CloseSvg,
  [BackBtnType.Finish]: FinishComp,
  [BackBtnType.AddIcon]: AddIcon,
  [BackBtnType.Question]: QuestionComp,
  [BackBtnType.CustomerService]: CustomerServiceComp,
  [BackBtnType.Add]: AddSvg,
  [BackBtnType.Edit]: EditSvg,
};

const typeIconSize = {
  [BackBtnType.Back]: 19,
  [BackBtnType.Close]: 24,
  [BackBtnType.Finish]: 17,
  [BackBtnType.AddIcon]: 24,
  [BackBtnType.Question]: 20,
  [BackBtnType.CustomerService]: 24,
  [BackBtnType.Add]: 24,
  [BackBtnType.Edit]: 24,
};

const defaultPageConfig: PageConfig = {
  leftFunc: noop,
  rightFunc: noop,
  title: '',
  leftBtnType: BackBtnType.Back,
  rightBtnType: null,
  layoutStyle: undefined,
  contentLayoutStyle: undefined,
  hideHeader: false,
  outerLayoutClassName: undefined, // 外部布局类名
  layoutClassName: undefined, // 布局的类名
  headerClassName: undefined, // 导航栏类名
  headerFontStyle: HeaderFontStyle.Dark,
  rightFontStyle: null,
  hideTitle: false,
  titleClassName: undefined,
  rightBtnClassName: undefined,
  whatsApp: undefined,
  capiConfig: undefined,
  headerForceDisplay: undefined,
};

const getSpecificTitle = (route?: RouteItem) => {
  let title = '';
  if (/pub\/en/.test(location.pathname)) {
    // pub 中的英文内容
    title = getEnValue(route?.meta.nameLangKey || '');
  }
  if (/pub\/zh/.test(location.pathname)) {
    // pub 中的中文内容
    title = getZhValue(route?.meta.nameLangKey || '');
  }
  return title || route?.meta.name;
};

function Layout(props: LayoutPropsType): JSX.Element {
  const {
    children,
    routes,
    modulePath,
    moduleHideHeader,
    moduleHideTopPadding,
    moduleHideWhatsApp = false,
  } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const [configPage, setPageConfig] = useState<PageConfig>(defaultPageConfig);
  const [currentRoute, setCurrentRoute] = useState<RouteItem>();

  const [statusBarHeight] = useSafeBarHeight();

  const [isPc] = usePcH5();

  const {
    leftFunc,
    rightFunc,
    title,
    leftBtnType,
    rightBtnType,
    layoutStyle,
    contentLayoutStyle,
    hideHeader,
    outerLayoutClassName,
    layoutClassName,
    headerClassName,
    headerFontStyle,
    rightFontStyle,
    hideTitle,
    whatsApp,
    pageHideTopPadding,
    titleClassName,
    rightBtnClassName,
    headerForceDisplay,
    capiConfig,
    rightBtn,
  } = configPage;

  const updateDocTitle = (args?: string | URL) => {
    const nextPathName = typeof args === 'string' ? args?.split('?')?.[0] : args?.pathname;
    const tempRoute = routes
      .reverse()
      .find((route) =>
        typeof route.path === 'string'
          ? new RegExp(`${route.path ? `${route.path.replace(/\/:\w+$/, '')}` : ''}`).test(
              nextPathName || ''
            )
          : undefined
      );
    if (tempRoute?.meta?.nameLangKey) {
      updateDocumentTitle(getEnValue(tempRoute?.meta?.nameLangKey));
    } else {
      updateDocumentTitle('CreFIT');
    }
  };

  // from native back
  // byClaude
  // byClaude-start
  const clickBack = async (fromNative?: boolean) => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('isNewWebview') === 'Y') {
      nativeApi.closeWindow();
      return;
    }
    if (fromNative && leftFunc === noop) {
      // 当没有回退按钮，将直接退出
      nativeApi.closeWindow();
      return;
    }
    const handleResult = await leftFunc?.();
    if (!handleResult) {
      // 处理返回的结果
      console.log('history1', history);
      if (history.state === null || history.state.idx === 0 || history.length === 1) {
        nativeApi.closeWindow();
      } else {
        navigate(-1);
      }
    }
  };
  // byClaude-end

  const clickRight = () => {
    rightFunc?.();
  };

  const leftIconSize = leftBtnType ? typeIconSize[leftBtnType] : 17;
  const marginL = 30 - leftIconSize;
  const rightIconSize =
    typeof rightBtnType === 'string' ? 17 : rightBtnType ? typeIconSize[rightBtnType] : 17;
  const marginR = 30 - rightIconSize;

  // 使用useMemo缓存px2vw计算结果，避免重复计算
  const memoizedPx2vw = useMemo(
    () => ({
      leftIconSize: px2vw(leftIconSize),
      leftIconHeight: px2vw(leftIconSize),
      marginL: px2vw(marginL),
      rightIconSize: px2vw(rightIconSize),
      rightIconHeight: px2vw(rightIconSize),
      marginR: px2vw(marginR),
      // 常用的固定值
      size17: px2vw(17),
      size24: px2vw(24),
      size13: px2vw(13),
      sizeNeg4: px2vw(-4),
    }),
    [leftIconSize, marginL, rightIconSize, marginR]
  );

  const LeftComp = leftBtnType ? typeIconMap[leftBtnType] : '';
  let rightEle = null;
  const rightCommonProps = {
    style: {
      width: memoizedPx2vw.rightIconSize,
      height: memoizedPx2vw.rightIconHeight,
      margin: `${memoizedPx2vw.marginR} 0 ${memoizedPx2vw.marginR} ${memoizedPx2vw.marginR}`,
    },
    color: rightFontStyle || headerFontStyle || getPrimaryColor(),
    onClick: clickRight,
    clsName: rightBtnClassName,
  };
  if (typeof rightBtnType === 'string') {
    rightEle = <TextComp text={rightBtnType} {...rightCommonProps} />;
  } else if (rightBtnType) {
    const Comp = typeIconMap[rightBtnType];
    rightEle = <Comp {...rightCommonProps} />;
  }
  if (rightBtn) {
    rightEle = rightBtn;
  }

  const updatePageConfig = useCallback(
    (config) => {
      const tempConfig = {
        ...defaultPageConfig,
        ...config,
      };
      setTimeout(() => {
        setPageConfig(tempConfig);
      }, 10);
    },
    [location.pathname]
  );

  const getPageConfig = useCallback(() => configPage, [configPage]);

  const layoutCompContextParams = useMemo(
    () => ({ updatePageConfig, getPageConfig }),
    [updatePageConfig, getPageConfig]
  );

  useLayoutEffect(() => {
    // 直接更新，不调方法，方法中的setTimeout是留给页面调用的
    setPageConfig(defaultPageConfig);
  }, [location.pathname]);

  useEffect(() => {
    const tempRoute = routes.find((route) =>
      route.path
        ? new RegExp(`${modulePath}/${route.path.replace(/\/:\w+$/, '')}`).test(location.pathname)
        : undefined
    );
    setCurrentRoute(tempRoute);
  }, [location.pathname]);

  useEffect(() => {
    updateDocTitle(location.pathname);
    sensors.quick('autoTrackSinglePage', { platform_type: 'H5', $ip: '' });
  }, [location.pathname]);

  useEffect(() => {
    kkBridgeRegwithdata(
      'onBackPressed',
      () => {
        clickBack(true);
      },
      true
    );
    return () => {
      kkBridgeUnRegwithdata('onBackPressed');
    };
  }, [leftFunc]);

  return (
    <>
      <div
        className={`${prefixCls} ${outerLayoutClassName || ''}`}
        style={{
          paddingTop: headerForceDisplay
            ? headerForceDisplay === ForceDisplay.ForceShow
              ? statusBarHeight
              : 0
            : moduleHideTopPadding || pageHideTopPadding
            ? 0
            : statusBarHeight,
          ...layoutStyle,
        }}
      >
        {(headerForceDisplay
          ? headerForceDisplay === ForceDisplay.ForceShow
          : !(hideHeader || moduleHideHeader)) && (
          <div className={`${headerClassName || ''} ${prefixCls}-header`}>
            {LeftComp && !isPc ? (
              <LeftComp
                color={headerFontStyle}
                style={{
                  width: memoizedPx2vw.leftIconSize,
                  height: memoizedPx2vw.leftIconHeight,
                  margin: `${memoizedPx2vw.marginL} ${memoizedPx2vw.marginL} ${memoizedPx2vw.marginL} 0`,
                }}
                onClick={() => clickBack(false)}
              />
            ) : (
              <div
                style={{
                  width: memoizedPx2vw.size17,
                  height: memoizedPx2vw.size17,
                  margin: `${memoizedPx2vw.size13} ${memoizedPx2vw.size13} ${memoizedPx2vw.size13} 0`,
                }}
              />
            )}
            {hideTitle ? (
              <div />
            ) : typeof title === 'string' ? (
              <div
                className={`${prefixCls}-header-title ${titleClassName}`}
                style={{ color: headerFontStyle }}
              >
                {title || getSpecificTitle(currentRoute)}
              </div>
            ) : (
              title || <div />
            )}
            {rightEle || (
              <div
                style={{
                  width: memoizedPx2vw.size17,
                  height: memoizedPx2vw.size17,
                  margin: `${memoizedPx2vw.size13} 0 ${memoizedPx2vw.size13} ${memoizedPx2vw.size13}`,
                }}
              />
            )}
          </div>
        )}
        <div
          className={`${layoutClassName || ''} ${prefixCls}-content`}
          style={{ ...contentLayoutStyle }}
        >
          <LayoutContext.Provider value={updatePageConfig}>
            <LayoutCompContext.Provider value={layoutCompContextParams}>
              {children}
            </LayoutCompContext.Provider>
          </LayoutContext.Provider>
        </div>
      </div>
      {(!location.pathname.includes('confirmLoan') || nativeApi.name !== Platform.Crefit) && (
        <FloatingAssistant
          whatsApp={whatsApp}
          moduleHideWhatsApp={moduleHideWhatsApp}
          capiConfig={capiConfig}
        />
      )}
    </>
  );
}
export default Layout;
// Contains claude-generated edits.
